import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBook,
    FaDiscord,
    FaGlobe,
    FaHeadset,
    FaQuestionCircle,
    FaShieldAlt,
    FaTelegramPlane,
    FaTerminal,
    FaTwitter
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiCode,
    TiDocumentText,
    TiGroup,
    TiLocationArrow,
    TiLockClosed,
    TiMessage,
    TiMessages
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import SupportTicketModal from './SupportTicketModal';
import GlassButton from "./ui/GlassButton";
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Support = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const stats = [
    { value: "24/7", label: "Global Availability", icon: <FaGlobe className="text-cyan-400" /> },
    { value: "< 15m", label: "Avg Response Time", icon: <FaHeadset className="text-purple-400" /> },
    { value: "99.9%", label: "Resolution Rate", icon: <FaShieldAlt className="text-emerald-400" /> },
    { value: "50k+", label: "Docs Pages", icon: <FaBook className="text-orange-400" /> },
  ];

  const supportCategories = [
    {
      title: "Help Center",
      description: "Everything you need to know about managing your account, security, and wallet features.",
      icon: <TiMessage />,
      color: "cyan",
      links: ["Account Setup", "Security Basics", "Wallet Guides"]
    },
    {
      title: "Technical Support",
      description: "Deep-dive assistance for node operators, API integrations, and blockchain infrastructure.",
      icon: <FaTerminal />,
      color: "purple",
      links: ["API Docs", "Node Setup", "Network Status"]
    },
    {
      title: "Documentation Library",
      description: "Comprehensive technical specifications, whitepapers, and ecosystem protocols.",
      icon: <TiDocumentText />,
      color: "emerald",
      links: ["Technical Specs", "Whitepapers", "Protocol Details"]
    },
    {
      title: "Security & Compliance",
      description: "Report vulnerabilities, access bug bounty programs, and review compliance audits.",
      icon: <TiLockClosed />,
      color: "orange",
      links: ["Bug Bounty", "Audit Reports", "Risk Portal"]
    },
    {
      title: "Developer Portal",
      description: "Build on Zero-Blockchain with our SDKs, libraries, and comprehensive sandbox environments.",
      icon: <TiCode />,
      color: "blue",
      links: ["SDK Library", "GitHub Repos", "Sandbox Access"]
    },
    {
      title: "Community Governance",
      description: "Participate in the future of the ecosystem through voting and proposal discussions.",
      icon: <TiGroup />,
      color: "pink",
      links: ["Active Proposals", "Voting Portal", "Forum Disc."]
    }
  ];

  const communitychannels = [
    { name: "Discord", icon: <FaDiscord />, members: "45k+", color: "#5865F2", desc: "Join our active community for real-time discussions." },
    { name: "Telegram", icon: <FaTelegramPlane />, members: "32k+", color: "#0088cc", desc: "Official announcements and community chat groups." },
    { name: "Twitter", icon: <FaTwitter />, members: "125k+", color: "#1DA1F2", desc: "Follow us for the latest news and ecosystem updates." },
  ];

  const knowledgeBase = [
    "How to migrate your tokens to Zero-Blockchain?",
    "Securing your multi-signature wallet",
    "Participating in the private sale phase",
    "Deploying your first smart contract",
    "Understanding the Zero-Coin tokenomics",
    "Setting up a validator node"
  ];

  const colorSchemes = {
    cyan: { border: "hover:border-cyan-500/50", icon: "text-cyan-400", bg: "bg-cyan-500/10", shadow: "shadow-cyan-500/10" },
    purple: { border: "hover:border-purple-500/50", icon: "text-purple-400", bg: "bg-purple-500/10", shadow: "shadow-purple-500/10" },
    emerald: { border: "hover:border-emerald-500/50", icon: "text-emerald-400", bg: "bg-emerald-500/10", shadow: "shadow-emerald-500/10" },
    orange: { border: "hover:border-orange-500/50", icon: "text-orange-400", bg: "bg-orange-500/10", shadow: "shadow-orange-500/10" },
    blue: { border: "hover:border-blue-500/50", icon: "text-blue-400", bg: "bg-blue-500/10", shadow: "shadow-blue-500/10" },
    pink: { border: "hover:border-pink-500/50", icon: "text-pink-400", bg: "bg-pink-500/10", shadow: "shadow-pink-500/10" },
  };

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/support-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="supportHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Ecosystem Support Hub"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Instant Assistance · Global Help-Desk · Dedicated Success
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-20 mx-auto max-w-7xl">
        
        {/* Introduction */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent">
              How Can We Assist You Today?
            </span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg leading-relaxed">
            Welcome to the Zero-Blockchain Support Hub. Whether you're a first-time user, 
            a developer, or an institution, we provide the tools and expertise to help you succeed.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-24">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative p-6 bg-white/5 border border-white/10 rounded-2xl text-center group hover:border-white/20 transition-all duration-300"
            >
              <div className="flex justify-center text-3xl mb-3">{stat.icon}</div>
              <div className="text-3xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Support Categories Section */}
        <div className="mb-32">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="max-w-xl">
              <h3 className="text-3xl font-bold text-white mb-4">Knowledge Categories</h3>
              <p className="text-gray-300">Select an area of interest to explore our curated documentation and support channels.</p>
            </div>
            <div className="hidden md:block">
              <GlassButton
                onClick={() => {}}
                className="px-6 py-2"
                variant="gray"
                size="sm"
              >
                Search Help Center
              </GlassButton>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {supportCategories.map((cat, index) => {
              const scheme = colorSchemes[cat.color];
              return (
                <motion.div
                  key={index}
                  ref={(el) => (sectionRefs.current[index] = el)}
                  className={`relative p-8 bg-black/50 backdrop-blur-sm border border-white/10 rounded-3xl overflow-hidden group transition-all duration-500 ${scheme.border} ${scheme.shadow}`}
                >
                  {/* Spotlight shadow */}
                  <div className={`absolute -top-24 -right-24 w-48 h-48 bg-gradient-radial from-${cat.color}-500/20 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  
                  <div className="relative z-10">
                    <div className={`w-14 h-14 rounded-2xl ${scheme.bg} flex items-center justify-center text-3xl ${scheme.icon} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      {cat.icon}
                    </div>
                    <h4 className="text-xl font-bold text-white mb-3">{cat.title}</h4>
                    <p className="text-gray-300 text-sm leading-relaxed mb-6">{cat.description}</p>
                    
                    <div className="space-y-3">
                      {cat.links.map((link, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-500 hover:text-white cursor-pointer group/link transition-colors">
                          <TiArrowRight className={`${scheme.icon} opacity-0 group-hover/link:opacity-100 -translate-x-2 group-hover/link:translate-x-0 transition-all`} />
                          <span>{link}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Community & Knowledge Section */}
        <div className="grid lg:grid-cols-3 gap-12 mb-32 items-start">
          
          {/* Community Channels */}
          <div className="lg:col-span-2 space-y-8">
            <h3 className="text-3xl font-bold text-white">Join the Community</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {communitychannels.map((chan, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  className="p-6 bg-white/5 border border-white/10 rounded-3xl"
                >
                  <div className="text-4xl mb-4" style={{ color: chan.color }}>{chan.icon}</div>
                  <h4 className="text-lg font-bold text-white">{chan.name}</h4>
                  <p className="text-xs text-gray-500 mt-1 mb-4">{chan.members} Members</p>
                  <p className="text-xs text-gray-300 leading-relaxed min-h-[48px]">{chan.desc}</p>
                  <div className="mt-6">
                    <GlassButton
                      onClick={() => {}}
                      className="w-full py-2"
                      variant="gray"
                      size="sm"
                    >
                      Connect Now
                    </GlassButton>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Quick Links / FAQ */}
          <div className="bg-black/80 border border-white/10 rounded-3xl p-8 sticky top-24">
            <h4 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <FaQuestionCircle className="text-purple-400" />
              Quick Links
            </h4>
            <div className="space-y-4">
              {knowledgeBase.map((link, index) => (
                <div key={index} className="group cursor-pointer flex items-start gap-3 border-b border-white/5 pb-4 last:border-0 hover:bg-white/5 transition-all p-2 rounded-lg">
                  <div className="mt-1 w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                  <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{link}</span>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <GlassButton
                onClick={() => {}}
                className="w-full py-4"
                variant="cyan"
                size="md"
              >
                View All FAQ
              </GlassButton>
            </div>
          </div>
        </div>

        {/* Premium CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-[3rem] overflow-hidden border border-white/10"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-transparent" />
          <div className="absolute inset-0 bg-black/60 backdrop-blur-xl" />
          
          {/* Animated Background Gradients */}
          <motion.div 
            animate={{ 
              x: [0, 50, 0], 
              y: [0, -30, 0],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-[100px]" 
          />
          <motion.div 
            animate={{ 
              x: [0, -50, 0], 
              y: [0, 30, 0],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-[100px]" 
          />

          <div className="relative z-10 py-20 px-8 text-center">
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Still Need Assistance?
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto mb-12 text-lg">
              Our support specialists are standing by to help with any complex architectural 
              or account issues. Choose your preferred contact method.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <GlassButton
                onClick={() => setIsSupportModalOpen(true)}
                className="px-10 py-5"
                variant="cyan"
                size="lg"
              >
                <div className="flex items-center gap-3 text-lg font-bold">
                  <TiMessages className="text-2xl" /> Open Support Ticket
                </div>
              </GlassButton>
              
              <GlassButton
                onClick={() => {
                  // Trigger global chat widget
                  const event = new CustomEvent('openAIChat');
                  window.dispatchEvent(event);
                }}
                className="px-10 py-5"
                variant="gray"
                size="lg"
              >
                <div className="flex items-center gap-3 text-lg font-bold">
                  <TiLocationArrow className="text-2xl" /> Live AI Chat
                </div>
              </GlassButton>
            </div>

            <div className="mt-12 flex items-center justify-center gap-8 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
              <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live Now</div>
              <div className="w-px h-4 bg-white/10" />
              <div>Multilingual Support</div>
              <div className="w-px h-4 bg-white/10" />
              <div>Enterprise Tier SLA</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
            BACK TO TOP
          </span>
        </div>
      )}

      {/* Footer */}
      <Footer />

      {/* Support Ticket Modal */}
      <SupportTicketModal 
        isOpen={isSupportModalOpen} 
        onClose={() => setIsSupportModalOpen(false)} 
      />
    </div>
  );
};

export default Support;
